











































import { Component, Vue, Watch } from 'vue-property-decorator';
import { google as Google } from 'google-maps';
import IconMyLocation from '@/components/icons/IconMyLocation.vue';
import { getGeolocationPermission, getGeolocationPosition, POSITION_ERROR_MESSAGE } from '@/utils/geolocation-utils';
import ActionTypes from '@/constants/action-types';
import { LocationPoint } from '@/types';
import { initializeGoogleMaps } from '@/utils/google-maps-utils';

type Item = {
  address: string;
};

const CURRENT_LOCATION = 'Current Location';

@Component({
  components: { IconMyLocation },
})
export default class AddressAutocomplete extends Vue {
  private model: string | null = null;

  private search: string | null = null;

  private positionErrorMessage: string | null = null;

  private items: Item[] = [];

  private isLoading = false;

  private google!: Google;

  private autocompleteService!: google.maps.places.AutocompleteService;

  private geocoder!: google.maps.Geocoder;

  private geolocationPermissionState!: PermissionState;

  mounted(): void {
    this.initMap();
    this.checkGeolocationPermissionState();
  }

  private async initMap(): Promise<void> {
    const google = await initializeGoogleMaps();
    this.google = google;
    this.autocompleteService = new google.maps.places.AutocompleteService();
    this.geocoder = new google.maps.Geocoder();
  }

  private async checkGeolocationPermissionState(): Promise<void> {
    this.geolocationPermissionState = (await getGeolocationPermission()).state;
  }

  get location() {
    return this.$store.state.location;
  }

  get isCurrentLocation(): boolean {
    return this.model === CURRENT_LOCATION;
  }

  @Watch('model')
  private onModelChange(model: string): void {
    if (model && !this.isCurrentLocation) {
      this.geocoder.geocode(
        { address: model },
        (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
          if (status !== this.google.maps.GeocoderStatus.OK) {
            return;
          }
          const location: LocationPoint = {
            latitude: results[0].geometry.location.lat(),
            longitude: results[0].geometry.location.lng(),
          };

          this.$store.commit(ActionTypes.SetLocation, location);
        },
      );
    }
  }

  @Watch('search')
  private searchInput(val: string): void {
    // clear error message on search
    if (this.positionErrorMessage) {
      this.positionErrorMessage = null;
    }
    // clear location on search
    if (!this.isCurrentLocation && this.location) {
      this.$store.commit(ActionTypes.SetLocation, null);
    }

    if (val && val !== this.model) {
      this.getPlacePredictions(val);
    } else if (!val) {
      this.items = [];
    }
  }

  private async onFindLocationClick(): Promise<void> {
    (this.$refs.autocomplete as any).isMenuActive = true; // eslint-disable-line
    await this.checkGeolocationPermissionState();

    if (this.geolocationPermissionState === 'denied') {
      this.positionErrorMessage = POSITION_ERROR_MESSAGE;
    } else {
      const timeout = this.geolocationPermissionState === 'granted' ? 2000 : 60 * 1000;
      this.isLoading = true;
      await this.setMyLocation({ timeout });
      this.isLoading = false;
    }
  }

  private async setMyLocation(options?: PositionOptions): Promise<void> {
    try {
      const position = await getGeolocationPosition(options);
      const location: LocationPoint = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      this.items = [{ address: CURRENT_LOCATION }];
      this.model = CURRENT_LOCATION;
      this.$store.commit(ActionTypes.SetLocation, location);

      (this.$refs.autocomplete as any).blur(); // eslint-disable-line
    } catch (e) {
      this.positionErrorMessage = e.message;
    }
  }

  private getPlacePredictions(input: string): void {
    this.isLoading = true;
    this.autocompleteService.getPlacePredictions(
      { input },
      (predictions, status): void => {
        this.isLoading = false;
        if (status !== this.google.maps.places.PlacesServiceStatus.OK) {
          return;
        }
        this.items = predictions.map((e) => ({ address: e.description }));
      },
    );
  }
}
